import { colors, HorizontalTabBar } from '@insurancemenu/imenu-components-lib';
import { ChevronLeft, ChevronRight } from '@insurancemenu/imenu-components-lib/dist/icons';
import FormsSpinner from 'components/common/loaders/FormSpinner';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { useRootStore } from 'store/rootStore';
import { isSubscriberValid } from 'global/utils/election/subscriberValidation';
import { smoothScrollToElement } from 'global/utils/smoothScrollToTop';
import SubscriberDependents from './SubscriberDependents';
import SubscriberElections from './SubscriberElections';
import SubscriberInfo from './SubscriberInfo';
import { BackButtonStyled, ButtonStyled, ButtonsWrapperStyled, SectionContentWrapperStyled } from './styles';
import { REVIEW_SUBSCRIBER_TABS_ID } from '../constants';

type SubscriberMapType = {
  [K: string]: JSX.Element;
};

export type SubscriberTab = 'INFO' | 'ELECTIONS' | 'DEPENDENTS';

const ReviewSubscriberContent: React.FC = observer(() => {
  const {
    reviewSubscriberStore: {
      loading,
      subscriber,
      requiredSubInfoPresenter,
      completeElectionChangeStatus,
      onBackRedirect,
    },
  } = useRootStore();

  const convertedSubscriber = subscriber ? toJS(subscriber) : undefined;
  const convertedRequiredSubInfo = requiredSubInfoPresenter ? toJS(requiredSubInfoPresenter) : undefined;

  const [activeTab, setActiveTab] = useState('ELECTIONS');

  if (!convertedSubscriber) return null;

  const SUBSCRIBER_MAP: SubscriberMapType = Object.freeze({
    ELECTIONS: <SubscriberElections subscriber={convertedSubscriber} />,
    INFO: <SubscriberInfo />,
    DEPENDENTS: <SubscriberDependents subscriber={convertedSubscriber} />,
  });

  const GENERAL_TABS_TITLES_MAP = [
    {
      id: 'ELECTIONS',
      label: 'Election',
      isValid: convertedRequiredSubInfo && isSubscriberValid(convertedRequiredSubInfo, convertedSubscriber),
    },
    { id: 'INFO', label: 'Employee Profile', isValid: true },
    { id: 'DEPENDENTS', label: 'Dependents', isValid: true },
  ];

  const onSaveStep = async (): Promise<void> => {
    await completeElectionChangeStatus(convertedSubscriber.id);
    onBackRedirect();
  };

  // External tabs switching
  const currentTabIndex = GENERAL_TABS_TITLES_MAP.findIndex((tab) => tab.id === activeTab);

  const switchTab = (direction: 'next' | 'back'): void => {
    const newIndex =
      direction === 'next'
        ? Math.min(currentTabIndex + 1, GENERAL_TABS_TITLES_MAP.length - 1)
        : Math.max(currentTabIndex - 1, 0);
    setActiveTab(GENERAL_TABS_TITLES_MAP[newIndex].id);
    smoothScrollToElement(REVIEW_SUBSCRIBER_TABS_ID);
  };

  const canGoBack = currentTabIndex > 0;
  const canGoNext = currentTabIndex < GENERAL_TABS_TITLES_MAP.length - 1;

  return (
    <div>
      <HorizontalTabBar tabs={GENERAL_TABS_TITLES_MAP} activeTabId={activeTab} id={REVIEW_SUBSCRIBER_TABS_ID}>
        <SectionContentWrapperStyled>
          <FormsSpinner display={loading} />
          {SUBSCRIBER_MAP[activeTab]}
        </SectionContentWrapperStyled>
      </HorizontalTabBar>
      <ButtonsWrapperStyled>
        {canGoBack ? (
          <BackButtonStyled
            color="primary"
            type="outlined"
            leftIconProp={<ChevronLeft />}
            size="large"
            width="24rem"
            onClick={(): void => switchTab('back')}
          >
            Back
          </BackButtonStyled>
        ) : (
          <ButtonStyled type="outlined" size="large" width="24rem" onClick={onBackRedirect}>
            Back to checklist
          </ButtonStyled>
        )}
        {canGoNext ? (
          <ButtonStyled
            color="primary"
            rightIconProp={<ChevronRight />}
            size="large"
            width="24rem"
            onClick={(): void => switchTab('next')}
            disabled={!GENERAL_TABS_TITLES_MAP[currentTabIndex].isValid}
          >
            Next
          </ButtonStyled>
        ) : (
          <ButtonStyled
            color="custom"
            customColor={colors.green[600]}
            size="large"
            width="24rem"
            onClick={onSaveStep}
            disabled={!GENERAL_TABS_TITLES_MAP[currentTabIndex].isValid}
          >
            Confirm
          </ButtonStyled>
        )}
      </ButtonsWrapperStyled>
    </div>
  );
});

export default ReviewSubscriberContent;
